import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateTestimonialMutation } from "../redux/services/api";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";
import { useLanguage } from "../contexts/LanguageContext";

const AddTestimonial = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const [createTestimonial, { isLoading: isSubmitting }] =
    useCreateTestimonialMutation();

  const [formData, setFormData] = useState({
    name: "",
    nameAr: "",
    content: "",
    contentAr: "",
    rating: 5,
    image: null,
  });
  const [saveStatus, setSaveStatus] = useState(null);
  const messageType = useMessageType(saveStatus);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleStarClick = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating: rating,
    }));
  };

  const handleHalfStarClick = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating: rating,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("nameAr", formData.nameAr);
      formDataToSend.append("content", formData.content);
      formDataToSend.append("contentAr", formData.contentAr);
      formDataToSend.append("rating", formData.rating);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      const result = await createTestimonial(formDataToSend).unwrap();

      if (result.success) {
        setSaveStatus(t("addEditTestimonial.testimonialCreatedSuccessfully"));
        setTimeout(() => {
          navigate(getLocalizedPath("/dashboard/testimonials"));
        }, 1500);
      } else {
        setSaveStatus(t("addEditTestimonial.errorCreatingTestimonial"));
      }
    } catch (error) {
      console.error("Error creating testimonial:", error);
      setSaveStatus(
        error.data?.message || t("addEditTestimonial.errorCreatingTestimonial")
      );
    }
  };

  const handleCancel = () => {
    navigate(getLocalizedPath("/dashboard/testimonials"));
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isHalfStar = formData.rating === i - 0.5;
      const isFullStar = formData.rating >= i;
      const isPartialStar = formData.rating > i - 1 && formData.rating < i;

      stars.push(
        <span
          key={i}
          className="star-container"
          style={{
            position: "relative",
            display: "inline-block",
            cursor: "pointer",
            marginRight: "2px",
          }}
        >
          {/* Background star (always gray) */}
          <span
            className="dashboard-star"
            style={{
              cursor: "pointer",
              color: "#666",
              position: "relative",
              zIndex: 1,
            }}
          >
            ★
          </span>

          {/* Foreground star (colored when selected) */}
          <span
            className={`dashboard-star ${
              isFullStar || isHalfStar ? "filled" : ""
            }`}
            onClick={() => handleStarClick(i)}
            style={{
              cursor: "pointer",
              position: "absolute",
              left: 0,
              top: 0,
              zIndex: 2,
              color: isFullStar || isHalfStar ? "#ffd700" : "transparent",
              overflow: isHalfStar ? "hidden" : "visible",
              width: isHalfStar ? "50%" : "100%",
            }}
          >
            ★
          </span>

          {/* Half star click area (invisible) */}
          <span
            className="half-star-click-area"
            onClick={(e) => {
              e.stopPropagation();
              handleHalfStarClick(i - 0.5);
            }}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "50%",
              height: "100%",
              cursor: "pointer",
              zIndex: 3,
              backgroundColor: "transparent",
            }}
          />

          {/* Full star click area (invisible) */}
          <span
            className="full-star-click-area"
            onClick={() => handleStarClick(i)}
            style={{
              position: "absolute",
              left: "50%",
              top: 0,
              width: "50%",
              height: "100%",
              cursor: "pointer",
              zIndex: 3,
              backgroundColor: "transparent",
            }}
          />
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1>{t("addEditTestimonial.addNewTestimonial")}</h1>
        <button className="btn btn-secondary" onClick={handleCancel}>
          {t("addEditTestimonial.backToTestimonials")}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-group">
          <label htmlFor="name">
            {t("addEditTestimonial.customerNameEnglish")} *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            placeholder={t("addEditTestimonial.enterCustomerName")}
          />
        </div>

        <div className="form-group">
          <label htmlFor="nameAr">
            {t("addEditTestimonial.customerNameArabic")}
          </label>
          <input
            type="text"
            id="nameAr"
            name="nameAr"
            value={formData.nameAr}
            onChange={handleInputChange}
            placeholder={t("addEditTestimonial.enterCustomerNameArabic")}
            dir="rtl"
          />
        </div>

        <div className="form-group">
          <label htmlFor="content">
            {t("addEditTestimonial.testimonialContentEnglish")} *
          </label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            required
            rows="6"
            placeholder={t("addEditTestimonial.enterTestimonialContent")}
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="contentAr">
            {t("addEditTestimonial.testimonialContentArabic")}
          </label>
          <textarea
            id="contentAr"
            name="contentAr"
            value={formData.contentAr}
            onChange={handleInputChange}
            rows="6"
            placeholder={t("addEditTestimonial.enterTestimonialContentArabic")}
            dir="rtl"
          ></textarea>
        </div>

        <div className="form-group">
          <label>{t("addEditTestimonial.rating")} *</label>
          <div className="dashboard-star-rating-input">
            {renderStars()}
            <span className="dashboard-rating-text">
              {formData.rating} star
              {formData.rating !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="image">
            {t("addEditTestimonial.testimonialImage")}
          </label>
          <input
            type="file"
            id="image"
            name="image"
            onChange={handleImageChange}
            accept="image/*"
          />
          {formData.image && (
            <div className="dashboard-image-preview">
              <img
                src={URL.createObjectURL(formData.image)}
                alt="Preview"
                style={{
                  maxWidth: "200px",
                  maxHeight: "200px",
                }}
              />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("addEditTestimonial.creating")
              : t("addEditTestimonial.createTestimonial")}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            {t("addEditTestimonial.cancel")}
          </button>
        </div>

        <StatusMessage
          message={saveStatus}
          type={messageType}
          show={!!saveStatus}
          autoClose={messageType === "success"}
          autoCloseDelay={3000}
          onClose={() => setSaveStatus(null)}
        />
      </form>
    </div>
  );
};

export default AddTestimonial;
